import React from 'react';
import { FaCalendarAlt, FaUsers, FaClock, FaCommentDots } from 'react-icons/fa';

/**
 * Event date / recruiters / meeting hours / after-hours bar shared by the
 * booth waiting area and the employer page, so both show the same details.
 */

export function formatEventDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function toValidDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatBoothTime(value) {
  const date = toValidDate(value);
  if (!date) return '—';
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatBoothDateTime(value) {
  const date = toValidDate(value);
  if (!date) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function isSameCalendarDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

export function formatTimeRange(startValue, endValue) {
  const start = toValidDate(startValue);
  const end = toValidDate(endValue);
  if (!start && !end) return '—';
  if (!start) return formatBoothTime(end);
  if (!end) return formatBoothTime(start);
  // Clock times alone are misleading once a window spans days: an Aug 27 close
  // time reads as if it already passed earlier today.
  if (!isSameCalendarDay(start, end)) {
    return `${formatBoothDateTime(start)} - ${formatBoothDateTime(end)}`;
  }
  return `${formatBoothTime(start)} - ${formatBoothTime(end)}`;
}

export default function BoothAvailabilityInfo({ booth, event }) {
  if (!booth) return null;
  const recruitersCountLabel = typeof booth.assignedRecruitersCount === 'number'
    ? String(booth.assignedRecruitersCount)
    : '—';
  const items = [
    { key: 'date', Icon: FaCalendarAlt, label: 'Event date', value: formatEventDate(event?.start) },
    { key: 'recruiters', Icon: FaUsers, label: 'Available Recruiters', value: recruitersCountLabel },
    { key: 'hours', Icon: FaClock, label: 'Meeting hours', value: formatTimeRange(booth.openTime, booth.recruiterEndTime) },
    {
      key: 'after',
      Icon: FaCommentDots,
      label: 'After hours messaging video, audio or text',
      value: formatTimeRange(booth.recruiterEndTime, booth.closeTime)
    },
  ];

  return (
    <section className="booth-availability-info-bar" aria-label="Booth availability information">
      {items.map(({ key, Icon, label, value }) => (
        <div key={key} className="booth-availability-info-item">
          <Icon className="booth-availability-info-icon" aria-hidden="true" />
          <div className="booth-availability-info-copy">
            <span className="booth-availability-info-label">{label}</span>
            <span className="booth-availability-info-value">{value}</span>
          </div>
        </div>
      ))}
    </section>
  );
}
